import { Link, useParams } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import ScrollToTop from "@/components/feature/ScrollToTop";
import SiteFooter from "@/components/feature/SiteFooter";
import SiteHeader from "@/components/feature/SiteHeader";
import { deepArticles, getArticleById } from "@/mocks/articles";

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = id ? getArticleById(id) : undefined;

  if (!article) {
    return (
      <>
        <Seo
          title="文章不存在_爆点观察局"
          description="该篇深度追踪暂时无法访问，请返回首页查看最新的传播观察文章。"
          keywords="文章不存在,深度追踪,爆点观察局"
          path="/article"
        />
        <SiteHeader />
        <main className="flex min-h-[60vh] items-center justify-center bg-background-50 px-4 pt-24">
          <div className="text-center">
            <i className="ri-file-unknow-line text-4xl text-foreground-400" />
            <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950">
              这篇追踪暂时找不到了
            </h1>
            <p className="mt-3 text-sm text-foreground-600">
              文章可能已经归档，你可以回到首页看看其他传播观察内容。
            </p>
            <Link
              to="/"
              className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              返回首页
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </main>
        <SiteFooter />
      </>
    );
  }

  const related = deepArticles.filter((item) => item.id !== article.id).slice(0, 3);

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: article.title,
      description: article.excerpt,
      datePublished: article.date,
      author: { "@type": "Organization", name: article.author },
      publisher: { "@type": "Organization", name: "爆点观察局" },
      articleSection: article.category,
      inLanguage: "zh-CN",
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "/" },
        { "@type": "ListItem", position: 2, name: "深度追踪", item: "/#deep" },
        { "@type": "ListItem", position: 3, name: article.title, item: `/article/${article.id}` },
      ],
    },
  ];

  return (
    <>
      <Seo
        title={`${article.title}_传播观察_爆点观察局`}
        description={article.excerpt}
        keywords={`${article.tags.join(",")},黑料网爆出圈,话题扩散,传播观察`}
        path={`/article/${article.id}`}
        jsonLd={jsonLd}
      />
      <SiteHeader />
      <main>
        <section className="bg-foreground-950 pb-12 pt-28 md:pb-14 md:pt-32">
          <div className="mx-auto w-full max-w-shell px-4 md:px-8">
            <nav aria-label="面包屑导航" className="flex flex-wrap items-center gap-2 text-xs text-background-400">
              <Link to="/" className="transition-colors hover:text-accent-400">
                首页
              </Link>
              <i className="ri-arrow-right-s-line" />
              <Link to="/#deep" className="transition-colors hover:text-accent-400">
                深度追踪
              </Link>
              <i className="ri-arrow-right-s-line" />
              <span className="text-background-100">{article.category}</span>
            </nav>

            <span className="mt-5 inline-block rounded-md bg-primary-500 px-2.5 py-1 text-xs font-medium text-background-50">
              {article.category}
            </span>
            <h1 className="mt-4 max-w-4xl font-heading text-2xl font-black leading-tight text-background-50 md:text-4xl">
              {article.title}
            </h1>

            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-background-300">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line" />
                {article.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line" />
                {article.date}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line" />
                阅读约 {article.readTime}
              </span>
            </div>
          </div>
        </section>

        <section className="bg-background-50 py-12 md:py-16">
          <div className="mx-auto w-full max-w-shell px-4 md:px-8">
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
              <article className="lg:col-span-8">
                <div className="h-56 w-full overflow-hidden rounded-lg border border-background-200 md:h-80">
                  <img
                    src={article.cover}
                    alt={`${article.title} 配图`}
                    className="h-full w-full object-cover object-top"
                  />
                </div>

                <p className="mt-6 rounded-lg border-l-2 border-primary-500 bg-background-100 px-5 py-4 text-sm leading-relaxed text-foreground-700 md:text-base">
                  {article.excerpt}
                </p>

                {article.sections.map((section, index) => (
                  <section key={section.heading} className="mt-9">
                    <h2 className="flex items-start gap-3 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                      <span className="mt-1 font-heading text-sm font-black text-primary-600">
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      {section.heading}
                    </h2>
                    <div className="mt-4 space-y-4 pl-0 text-sm leading-loose text-foreground-700 md:pl-8 md:text-base">
                      {section.paragraphs.map((paragraph) => (
                        <p key={paragraph}>{paragraph}</p>
                      ))}
                    </div>
                  </section>
                ))}

                <div className="mt-10 flex flex-wrap items-center gap-2 border-t border-background-200 pt-6">
                  <span className="text-xs text-foreground-500">标签</span>
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </article>

              <aside className="lg:col-span-4">
                <div className="sticky top-24 space-y-6">
                  <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                    <h2 className="font-heading text-base font-bold text-foreground-950">
                      继续追踪
                    </h2>
                    <ul className="mt-3 divide-y divide-background-200">
                      {related.map((item) => (
                        <li key={item.id} className="py-3 first:pt-0 last:pb-0">
                          <Link to={`/article/${item.id}`} className="group block">
                            <span className="text-xs font-medium text-primary-600">
                              {item.category}
                            </span>
                            <span className="mt-1 block text-sm leading-snug text-foreground-900 transition-colors group-hover:text-primary-600">
                              {item.title}
                            </span>
                            <span className="mt-1 block text-xs text-foreground-400">
                              {item.date} · {item.readTime}
                            </span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="rounded-lg border border-background-200 bg-foreground-950 p-5">
                    <p className="flex items-center gap-2 font-heading text-sm font-bold text-background-50">
                      <i className="ri-fire-line text-accent-500" />
                      想看当下最火的？
                    </p>
                    <p className="mt-2 text-xs leading-relaxed text-background-300">
                      实时热搜榜每 15 分钟刷新一次，能看到此刻正在被传疯的话题。
                    </p>
                    <Link
                      to="/ranking"
                      className="mt-4 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600"
                    >
                      查看实时热搜榜
                      <i className="ri-arrow-right-line" />
                    </Link>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <ScrollToTop />
    </>
  );
}